import { useEffect, useState } from 'react';

export interface CountdownBadgeProps {
  /** ISO timestamp when the buyback offer auto-fires. */
  autoSellAt: string | null;
  /**
   * ISO timestamp when the buyback offer was issued — anchors the pie
   * denominator (`autoSellAt - offerCreatedAt`). When absent we degrade
   * to a 24 h fallback window.
   */
  offerCreatedAt?: string | null;
}

/**
 * Fallback offer lifetime, used only when the real `offerCreatedAt` is
 * missing. Pie sizing then degrades to `remaining / 24h`, which matches
 * the backend's default offer window.
 */
const FALLBACK_DURATION_MS = 24 * 60 * 60 * 1000;

/**
 * Below this remaining fraction the remaining wedge flips from white to
 * the warning amber from the design (Figma 708:1891).
 */
const URGENT_THRESHOLD = 0.2;

/**
 * Glass chip with a circular pie indicator + relative countdown
 * ("20h 15m left" / "42m left") in the top-right corner of every
 * `OpenedItemCard`. Figma 708:1885 / 708:1891.
 *
 * The pie starts full and the elapsed wedge grows clockwise from 12 o'clock
 * as the buyback offer approaches `autoSellAt`:
 *   • full circle               → just-issued
 *   • thin remaining sliver     → about to auto-fire
 *   • absent                    → already expired
 * When `remaining < URGENT_THRESHOLD` the remaining wedge re-colours to
 * the amber warning tone.
 *
 * Self-ticking: re-renders exactly when the displayed minute flips (one
 * timeout per flip, not a per-second interval), so the label stays honest
 * without waiting for a parent re-render. The final flip past `autoSellAt`
 * unmounts the badge the same way.
 */
export function CountdownBadge({
  autoSellAt,
  offerCreatedAt,
}: CountdownBadgeProps) {
  const [tick, setTick] = useState(0);

  // Schedule the next re-render at the upcoming minute boundary of the
  // remaining time (+small epsilon so the recompute lands on the new
  // minute). Re-arms itself after every tick until expiry.
  useEffect(() => {
    if (!autoSellAt) return;
    const target = Date.parse(autoSellAt);
    if (!Number.isFinite(target)) return;
    const diffMs = target - Date.now();
    if (diffMs <= 0) return;
    const delay = (diffMs % 60_000) + 50;
    const timer = window.setTimeout(() => setTick((n) => n + 1), delay);
    return () => window.clearTimeout(timer);
  }, [autoSellAt, tick]);

  const data = computeCountdown(autoSellAt, offerCreatedAt ?? null);
  if (!data) return null;
  return (
    <div
      className="
        absolute right-2 top-2 z-10
        flex items-center gap-1 rounded-full
        py-0.5 pl-1.5 pr-1
        @[600px]:right-3 @[600px]:top-3
        @[600px]:gap-1.5 @[600px]:py-1 @[600px]:pl-2.5 @[600px]:pr-1.5
      "
      style={{
        background: 'var(--co-MyPacksItemCard-timerBg, rgba(255,255,255,0.15))',
        color: 'var(--co-MyPacksItemCard-timerText, #FFFFFF)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <span className="type-subhead-semi whitespace-nowrap text-[10px] @[600px]:text-[13px]">
        {data.label}
      </span>
      <PieProgress ratio={data.ratio} />
    </div>
  );
}

interface PieProgressProps {
  /** Remaining fraction in `[0, 1]`. 1 = full circle, 0 = empty. */
  ratio: number;
}

/**
 * 18×18 circular pie wedge. Starts fully filled and the *elapsed* portion
 * sweeps clockwise from 12 o'clock as a translucent track — the leftover
 * arc stays opaque. Past `URGENT_THRESHOLD` the leftover arc switches to
 * the amber warning tone (Figma 708:1891).
 */
function PieProgress({ ratio }: PieProgressProps) {
  const remaining = Math.max(0, Math.min(1, ratio));
  const elapsedDeg = (1 - remaining) * 360;
  const urgent = remaining < URGENT_THRESHOLD;
  const remainingColor = urgent
    ? 'var(--co-MyPacksItemCard-timerProgressUrgent, #DBB254)'
    : 'currentColor';
  const elapsedColor = urgent
    ? 'var(--co-MyPacksItemCard-timerProgressTrackUrgent, rgba(255,180,95,0.10))'
    : 'var(--co-MyPacksItemCard-timerProgressTrack, rgba(255,255,255,0.10))';
  return (
    <span
      aria-hidden
      className="block h-[12px] w-[12px] rounded-full @[600px]:h-[18px] @[600px]:w-[18px]"
      style={{
        background:
          `conic-gradient(${elapsedColor} 0deg ${elapsedDeg}deg,` +
          ` ${remainingColor} ${elapsedDeg}deg 360deg)`,
      }}
    />
  );
}

interface CountdownData {
  label: string;
  ratio: number;
}

function computeCountdown(
  autoSellAt: string | null,
  offerCreatedAt: string | null,
): CountdownData | null {
  if (!autoSellAt) return null;
  const target = Date.parse(autoSellAt);
  if (!Number.isFinite(target)) return null;
  const diffMs = target - Date.now();
  if (diffMs <= 0) return null;
  const totalMs = resolveTotalDuration(target, offerCreatedAt);
  return {
    label: formatLabel(diffMs),
    ratio: diffMs / totalMs,
  };
}

function resolveTotalDuration(targetMs: number, offerCreatedAt: string | null): number {
  if (offerCreatedAt) {
    const createdMs = Date.parse(offerCreatedAt);
    if (Number.isFinite(createdMs)) {
      const total = targetMs - createdMs;
      if (total > 0) return total;
    }
  }
  return FALLBACK_DURATION_MS;
}

function formatLabel(diffMs: number): string {
  const totalMinutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  if (hours < 1) return `${Math.max(1, totalMinutes)}m left`;
  if (hours < 48) {
    const mins = totalMinutes % 60;
    return mins > 0 ? `${hours}h ${mins}m left` : `${hours}h left`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d left`;
}
